import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { ToastContainer, toast } from 'react-toastify';

import { FinancialSummaryMath } from '../../FinancialSummaryMath/FinancialSummaryMath';
import { applySettlementEdits } from '../applySettlementEdits';
import SettlementTable from './SettlementTable';
import PartySignatureLines from './PartySignatureLines';
import PageFrame from '../../Settlement/components/PageFrame';
import { signatureInputStyle } from '../../Settlement/components/Settlement';

// V3 offer document runs on FinancialSummaryMath — the SAME engine as the V3
// editor — so the preview/PDF/email agree with the editor and V3 deal math
// can evolve without touching V2's SettlementMath (O-3115). Note vs the V2
// fork source: arg order is (offer, budget, isSettling) and calculate()
// returns a JSON string, so it must be parsed.
const getCalculated = (budget, isSettling, offer) => {
  const effectiveBudget = isSettling ? applySettlementEdits(budget) : budget;
  const calculator = new FinancialSummaryMath(offer, effectiveBudget, isSettling);
  return JSON.parse(calculator.calculate());
}

var _ = require('lodash');

const parseFloatFromString = (value) => {
  if(typeof(value) === "number"){
    return value;
  }

  return parseFloat(value.split(",").join(""));
}

const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

const CloseButton = ({ closeToast }) => (
  <button
    onClick={closeToast}
    style={{
      color: '#000',
      opacity: '0.3',
      background: 'transparent',
      border: 'none',
      fontSize: '24px',
      lineHeight: '1',
      position: 'absolute',
      top: '0px',
      right: '4px',
      cursor: 'pointer'
    }}
  >
    &times;
  </button>
);

const CustomToast = () => {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', lineHeight: '1.4' }}>
      <i className="fas fa-exclamation-circle ml-1 mr-3" style={{ color: '#F8981D', fontSize: '20px' }}></i>
      <div>
        <strong>Editing a ticket type will break live sync.</strong><br />
        Once edited, this value will no longer update automatically. Changes must be managed manually. You can undo this by deleting the override.
      </div>
    </div>
  )
}

const Settlement = ({
  budget,
  offer,
  confirm,
  confirmChanged,
  createSettlementLineItem,
  csrfToken,
  team,
  currentUser,
  offerId,
  deleteSettlementLineItem,
  updateSettlementLineItem,
  updateSettlementLineItemTaxWithholdingExemption,
  itemToEdit,
  itemToEditChanged,
  submitSettlementOverrideForm,
  deleteSettlementOverride,
  requestFormat,
  isSettling,
  isV3,
  editedDropCount,
  editedDropCountChanged,
  editedCompCount,
  editedCompCountChanged,
  editedKillCount,
  editedKillCountChanged,
  saveOverrides,
  isSaving,
  showPageFrame,
  offerChanged,
  isSavingChanged,
  getActiveTags,
  getAvailableTags,
}) => {
  const [budgetState, setBudgetState] = useState(budget);
  const prevBudget = usePrevious(budget);

  useEffect(() => {
    if (prevBudget && budget.settlement_overrides.length > prevBudget.settlement_overrides.length) {
      toast.warn(
        <CustomToast />, {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 10000,
        hideProgressBar: true,
        closeOnClick: true,
        draggable: false,
        style: {
          width: '500px',
          backgroundColor: '#fcf8e3',
          color: '#8a6d3b',
          border: '1px solid #faebcc',
          borderRadius: '8px'
        },
        closeButton: <CloseButton />
      });
    }
  }, [budget]);

  useEffect(() => {
    setBudgetState((current) => {
      return {
        ...budget,
        settlement_visibilities: current.settlement_visibilities
      };
    });
  }, [budget])

  // Recompute only when an input the engine reads actually changes. Every edit
  // replaces budgetState by reference (no in-place mutation), so live updates
  // still trickle through to the totals.
  const calculated = useMemo(
    () => getCalculated(budgetState, isSettling, offer),
    [budgetState, isSettling, offer]
  );

  const confirmObj = confirm ? confirm : {
    id: offer.calendar_event_id,
    name: offer.event_name,
    calendar_classification: "offer",
    human_metadata: offer.human_metadata,
    canonical_tags: offer.canonical_tags || [],
    buyer: offer.buyer,
    promoter: offer.promoter,
    venue: offer.venue,
    venue_ownership: offer.venue_ownership,
    rooms: offer.rooms || []
  };

  const poolMembers = offer.pool_members || [];
  const poolSignatureParties = (
    !isSettling
      && offer.pool_mode === true
      && team.package?.multi_artist_v3_offers
      && poolMembers.length > 1
      && poolMembers.some((member) => member.v3_offer_id === offer.id)
  ) ? ["Venue", ...poolMembers.map((member) => member.performer_name || "Unnamed artist")] : null;

  const handleConfirmUpdate = (updatedConfirm) => {
    if (confirmChanged) {
      confirmChanged(updatedConfirm);
    }
  };

  // Open the editor the offer was last saved in (O-3412). editor_variant is
  // persisted on save; unset (legacy) falls back to the simple editor.
  const editorVariant = offer?.editor_variant || 'v3_simple';
  const editOfferPath = editorVariant === 'v3_simple'
    ? `/teams/${team.id}/offers/v3_simple/${offerId}/edit`
    : `/teams/${team.id}/offers/v3/${offerId}/edit`;

  return (
    <PageFrame  csrfToken={csrfToken}
                currentUser={currentUser}
                confirm={confirmObj}
                onConfirmChanged={handleConfirmUpdate}
                showPageFrame={showPageFrame}
                offerId={offerId}
                editOfferPath={editOfferPath}
                isSettling={isSettling}
                requestFormat={requestFormat}
                isSaving={isSaving}
                isV3={isV3}
                team={team}>
      <>
        <ToastContainer icon={false} style={{ width: "auto" }} />
        <form onSubmit={
                (e) => {
                  e.preventDefault();

                  if(editedDropCount !== null) {
                    var dropCount = (
                      editedDropCount === "" ? "0" : parseFloatFromString(editedDropCount)
                    );

                    saveOverrides(csrfToken, team, offerId, budget, { drop_count_override: dropCount });
                  } else if(editedCompCount !== null){
                    var compCount = (
                      editedCompCount === "" ? "0" : parseFloatFromString(editedCompCount)
                    );

                    saveOverrides(csrfToken, team, offerId, budget, { comp_count_override: compCount });
                  } else if(editedKillCount !== null){
                    var killCount = (
                      editedKillCount === "" ? "0" : parseFloatFromString(editedKillCount)
                    );

                    saveOverrides(csrfToken, team, offerId, budget, { kill_count_override: killCount });
                  }else if(itemToEdit.overridable_type === "SettlementLineItem"){
                    updateSettlementLineItem(csrfToken, team, itemToEdit);
                  } else {
                    submitSettlementOverrideForm(csrfToken, team, offerId, itemToEdit);
                  }
                }
              }>
          <SettlementTable budget={budget}
                          setBudgetState={setBudgetState}
                          offer={offer}
                          calculated={calculated}
                          isSettling={isSettling}
                          editedDropCount={editedDropCount}
                          editedDropCountChanged={editedDropCountChanged}
                          editedCompCount={editedCompCount}
                          editedCompCountChanged={editedCompCountChanged}
                          editedKillCount={editedKillCount}
                          editedKillCountChanged={editedKillCountChanged}
                          saveOverrides={saveOverrides}
                          confirm={confirm}
                          createSettlementLineItem={createSettlementLineItem}
                          deleteSettlementLineItem={deleteSettlementLineItem}
                          updateSettlementLineItemTaxWithholdingExemption={updateSettlementLineItemTaxWithholdingExemption}
                          requestFormat={requestFormat}
                          csrfToken={csrfToken}
                          team={team}
                          currentUser={currentUser}
                          offerId={offerId}
                          itemToEdit={itemToEdit}
                          itemToEditChanged={itemToEditChanged}
                          isSaving={isSaving}
                          offerChanged={offerChanged}
                          isSavingChanged={isSavingChanged}
                          deleteSettlementOverride={deleteSettlementOverride}
                          getActiveTags={getActiveTags}
                          getAvailableTags={getAvailableTags} />
        </form>
        {offer.in_memory_contacts && offer.in_memory_contacts.length > 0 ? (
          <table style={{width: "100%", marginTop: "14px", fontFamily: "Open Sans", fontSize: "14px"}}>
            <tbody>
              <tr>
                <td>
                  <div style={{ marginTop: "8px", padding: "0px 7px" }}>
                    <table style={{
                       width: "100%",
                       borderCollapse: "separate",
                       borderSpacing: "0px 2px",
                       tableLayout: "fixed"
                    }}>
                      <thead style={{"textAlign": "left"}}>
                        <tr style={{"background": "var(--od-c-f5f5f5, #F5F5F5)"}}>
                          <th style={{
                            "fontSize": "14px",
                            "color": "var(--od-c-000000, #000000)",
                            "padding": "6px 15px",
                            "textAlign": "left"
                          }}>
                            <strong>Contacts</strong>
                          </th>
                           <th style={{
                             "fontWeight": "600",
                             "fontSize": "14px",
                             "color": "var(--od-c-999999, #999999)",
                             "textAlign": "left",
                             "padding": "6px 15px 6px 0px"
                           }}>
                             TYPE
                           </th>
                           <th style={{
                             "fontWeight": "600",
                             "fontSize": "14px",
                             "color": "var(--od-c-999999, #999999)",
                             "textAlign": "left",
                             "padding": "6px 15px 6px 0px"
                           }}>
                             EMAIL
                           </th>
                           <th style={{
                             "fontWeight": "600",
                             "fontSize": "14px",
                             "color": "var(--od-c-999999, #999999)",
                             "textAlign": "left",
                             "padding": "6px 15px 6px 0px"
                           }}>
                             PHONE
                           </th>
                        </tr>
                      </thead>
                      <tbody style={{"fontSize": "14px"}}>
                        {offer.in_memory_contacts
                          .slice()
                          .sort((a, b) => {
                            const nameA = `${a.first_name || ''} ${a.last_name || ''}`.trim().toLowerCase();
                            const nameB = `${b.first_name || ''} ${b.last_name || ''}`.trim().toLowerCase();
                            return nameA.localeCompare(nameB);
                          })
                          .map((contact, index) => {
                          const fullName = [contact.first_name, contact.last_name].filter(Boolean).join(' ');
                          const formattedPhone = contact.phone ? (() => {
                            if (contact.phone.length === 10) {
                              return `(${contact.phone.slice(0, 3)}) ${contact.phone.slice(3, 6)}-${contact.phone.slice(6)}`;
                            }
                            return contact.phone;
                          })() : '--';

                          return (
                            <tr key={contact.id || index}>
                              <td style={{
                                padding: "3px 15px",
                                verticalAlign: "middle",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                width: requestFormat === "pdf" ? "25%" : "25%"
                              }}
                              title={fullName}>
                                {fullName}
                              </td>
                              <td style={{
                                padding: "3px 8px 3px 0px",
                                verticalAlign: "middle",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                width: "20%"
                              }}
                              title={contact.contact_type || '--'}>
                                {contact.contact_type || '--'}
                              </td>
                              <td style={{
                                padding: "3px 8px 3px 0px",
                                verticalAlign: "middle",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                width: requestFormat === "pdf" ? "50%" : "35%"
                              }}
                              title={contact.email || '--'}>
                                {contact.email || '--'}
                              </td>
                              <td style={{
                                padding: "3px 8px 3px 0px",
                                verticalAlign: "middle",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                width: requestFormat === "pdf" ? "25%" : "20%"
                              }}
                              title={formattedPhone}>
                                {formattedPhone}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        ) : null}
        <table style={{width: "100%", fontFamily: "Open Sans", fontSize: "14px"}}>
          <tbody>
            <tr>
              <td style={{ padding: "0px 7px" }}>
                {offer.additional_terms_no_tags.length > 0 ? (
                  <>
                    <hr style={{
                      height: "1px",
                      borderTop: "none",
                      borderBottom: "2px solid var(--od-c-e6e6e6, #e5e5e5)",
                      borderLeft: "none",
                      borderRight: "none",
                      margin: "0 0 20px 0",
                      paddingTop: "20px",
                      background: "var(--od-c-ffffff, white)",
                      clear: "both"
                    }} />
                    <h3 style={{margin: "16px 0px", fontSize: "16px"}}>
                      <strong>Additional Terms</strong>
                    </h3>
                    {/* Additional terms are sanitized on the server */}
                    <div
                      dangerouslySetInnerHTML={{__html: offer.sanitized_additional_terms}}
                    />
                  </>
                ) : null}
              </td>
            </tr>
          </tbody>
        </table>
        {(isSettling && offer.venue_ownership?.show_signature_lines_on_settlement) || (!isSettling && offer.venue_ownership?.show_signature_lines_on_offer) ? (
          <div className="mt-3" style={{ padding: "0px 7px", pageBreakInside: "avoid" }}>
            <p>
              <strong>
                It is confirmed that we have read, understood and approve of the terms and conditions set forth here.
              </strong>
            </p>
            {poolSignatureParties ? <PartySignatureLines parties={poolSignatureParties} /> : null}
            {!poolSignatureParties ? (
            <table style={{width: "100%", fontSize: "14px"}}>
              <colgroup>
                <col style={{width: "50%"}} />
                <col style={{width: "50%"}} />
              </colgroup>
              <tbody>
                <tr>
                  <td>
                    <div style={{paddingRight: "10px"}}>
                      <label>Name / Company</label>
                      <input
                        type="text"
                        style={signatureInputStyle}
                      />
                    </div>
                  </td>
                  <td>
                    <div style={{paddingLeft: "10px"}}>
                      <label>Name / Company</label>
                      <input
                        type="text"
                        style={signatureInputStyle}
                      />
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <div style={{paddingRight: "10px"}}>
                      <label>Phone / Email</label>
                      <input
                        type="text"
                        style={signatureInputStyle}
                      />
                    </div>
                  </td>
                  <td>
                    <div style={{paddingLeft: "10px"}}>
                      <label>Phone / Email</label>
                      <input
                        type="text"
                        style={signatureInputStyle}
                      />
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <div style={{paddingRight: "10px"}}>
                      <label>Signature</label>
                      <input
                        type="text"
                        style={signatureInputStyle}
                      />
                    </div>
                  </td>
                  <td>
                    <div style={{paddingLeft: "10px"}}>
                      <label>Signature</label>
                      <input
                        type="text"
                        style={signatureInputStyle}
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            ) : null}
          </div>
        ) : null}
      </>
    </PageFrame>
  );
};

Settlement.propTypes = {
  budget: PropTypes.object.isRequired,
  offer: PropTypes.object.isRequired,
  confirm: PropTypes.object,
  confirmChanged: PropTypes.func,
  createSettlementLineItem: PropTypes.func.isRequired,
  csrfToken: PropTypes.string,
  team: PropTypes.object.isRequired,
  currentUser: PropTypes.object,
  offerId: PropTypes.string.isRequired,
  deleteSettlementLineItem: PropTypes.func.isRequired,
  updateSettlementLineItem: PropTypes.func.isRequired,
  updateSettlementLineItemTaxWithholdingExemption: PropTypes.func,
  itemToEdit: PropTypes.object,
  itemToEditChanged: PropTypes.func.isRequired,
  submitSettlementOverrideForm: PropTypes.func.isRequired,
  deleteSettlementOverride: PropTypes.func.isRequired,
  requestFormat: PropTypes.string,
  isSettling: PropTypes.bool,
  isV3: PropTypes.bool,
  editedDropCount: PropTypes.string,
  editedDropCountChanged: PropTypes.func.isRequired,
  editedCompCount: PropTypes.string,
  editedCompCountChanged: PropTypes.func.isRequired,
  editedKillCount: PropTypes.string,
  editedKillCountChanged: PropTypes.func.isRequired,
  saveOverrides: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
  showPageFrame: PropTypes.bool,
  offerChanged: PropTypes.func.isRequired,
  isSavingChanged: PropTypes.func.isRequired,
  getActiveTags: PropTypes.func.isRequired,
  getAvailableTags: PropTypes.func.isRequired,
};

export default Settlement;
